/**
 * Bandeau "Alerte active" — Phase 2A étendue L15b.
 *
 * Affiche un bandeau coloré en haut d'écran si une alerte municipale est en cours.
 * Couleurs adaptées au niveau :
 *   info   → vert (success)
 *   avis   → ambre (warning)
 *   alerte → rouge (urgent)
 *   crise  → noir + clignotement (crise)
 *
 * Visible par TOUS les utilisateurs connectés (y compris administrés) via /api/grc/alertes/active.
 * Polling toutes les 60s pour mise à jour quasi temps-réel sans WebSocket.
 *
 * Le bandeau est dismissible mais re-apparaît si une nouvelle alerte plus haute arrive.
 *
 * Refonte R3 (mai 2026) :
 *   - Bandeau en glass avec gradient horizontal du niveau + border-l 3px du ton
 *   - Icône avec halo coloré subtil
 *   - Bouton "Détails" en pill glass (chevron lucide)
 *   - Bouton dismiss en variant ghost
 *   - aria-live="assertive" conservé
 *
 * Fix R13.6 (21 mai 2026) :
 *   - Le sessionStorage `vpc:alerte-dismissed` était partagé entre tous les comptes
 *     du même onglet. Quand un manager dismiss une alerte, l'administré dans le
 *     même onglet ne la voyait plus. Fix : key liée à user.id.
 *   - Clear automatique au login/logout (cf. auth-context.tsx).
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiException } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';                        // R13.6 — pour key sessionStorage par-user
import { AlertTriangle, AlertOctagon, Info, Bell, X, ChevronRight } from 'lucide-react';
import type { GrcAlerte } from '@roronoa-collectivites/shared-types';

interface BannerStyle {
  /** Fond glass avec gradient. */
  bg: string;
  /** Couleur de texte (foreground). */
  text: string;
  /** Border-left 3px colorée. */
  borderL: string;
  /** Couleur de l'icône (avec halo). */
  iconColor: string;
  Icon: typeof Info;
  label: string;
}

const BANNER_STYLE: Record<string, BannerStyle> = {
  info: {
    bg: 'bg-gradient-to-r from-success/90 via-success/75 to-success/85',
    text: 'text-white',
    borderL: 'border-l-success',
    iconColor: 'text-white',
    Icon: Info,
    label: 'INFORMATION',
  },
  avis: {
    bg: 'bg-gradient-to-r from-warning/95 via-warning/80 to-warning/90',
    text: 'text-white',
    borderL: 'border-l-warning',
    iconColor: 'text-white',
    Icon: Bell,
    label: 'AVIS',
  },
  alerte: {
    bg: 'bg-gradient-to-r from-urgent via-urgent/80 to-urgent/90',
    text: 'text-white',
    borderL: 'border-l-urgent',
    iconColor: 'text-white',
    Icon: AlertTriangle,
    label: 'ALERTE',
  },
  crise: {
    bg: 'bg-gradient-to-r from-ink via-ink/90 to-ink',
    text: 'text-white',
    borderL: 'border-l-urgent',
    iconColor: 'text-urgent',
    Icon: AlertOctagon,
    label: 'CRISE',
  },
};

export function ActiveAlerteBanner() {
  const { user } = useAuth();                                       // R13.6 — pour key per-user
  const userId = user?.id ?? null;
  // R13.6 — key sessionStorage liée à user.id. Si pas d'user (déconnecté), pas de key.
  const dismissKey = userId ? `vpc:alerte-dismissed:${userId}` : null;

  const [alerte, setAlerte] = useState<GrcAlerte | null>(null);
  const [dismissedId, setDismissedId] = useState<string | null>(() => {
    if (typeof window === 'undefined' || !dismissKey) return null;
    try {
      return sessionStorage.getItem(dismissKey) ?? null;
    } catch {
      return null;
    }
  });

  // R13.6 — Resync dismissedId si user change (login/logout/changement compte)
  useEffect(() => {
    if (typeof window === 'undefined' || !dismissKey) {
      setDismissedId(null);
      return;
    }
    try {
      setDismissedId(sessionStorage.getItem(dismissKey) ?? null);
    } catch {
      setDismissedId(null);
    }
  }, [dismissKey]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await api.grc.alertes.active();
        if (!cancelled) setAlerte(res.alerte);
      } catch (err) {
        // 401/403 (non connecté) : on n'affiche rien, normal
        if (err instanceof ApiException && (err.status === 401 || err.status === 403)) {
          return;
        }
        // eslint-disable-next-line no-console
        console.warn('Échec chargement alerte active:', err);
      }
    }

    void load();
    const interval = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  if (!alerte) return null;
  if (dismissedId === alerte.id) return null;

  const style = BANNER_STYLE[alerte.niveau] ?? BANNER_STYLE.info!;
  const isCrise = alerte.niveau === 'crise';

  function dismiss() {
    if (!alerte) return;
    setDismissedId(alerte.id);
    // R13.6 — key liée à user.id (si user connecté)
    if (dismissKey) {
      try {
        sessionStorage.setItem(dismissKey, alerte.id);
      } catch { /* ignore */ }
    }
  }

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={[
        'relative border-l-4 backdrop-blur-glass shadow-soft',
        style.bg,
        style.text,
        style.borderL,
        isCrise ? 'animate-pulse' : '',
      ].join(' ')}
    >
      <div className="mx-auto flex max-w-[1400px] items-center gap-3 px-4 py-2.5">
        {/* Icône avec halo coloré subtil */}
        <div className="relative flex-shrink-0">
          <style.Icon className={`h-5 w-5 ${style.iconColor}`} aria-hidden="true" />
          {!isCrise && (
            <span
              aria-hidden="true"
              className="absolute inset-0 -m-1 rounded-full bg-white/15 blur-md"
            />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="rounded-r-md bg-white/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-r-eyebrow backdrop-blur-glass">
              {style.label}
            </span>
            <span className="text-sm font-semibold">{alerte.titre}</span>
          </div>
          <p className="text-xs opacity-95 truncate">{alerte.message}</p>
        </div>

        <Link
          to={
            ['super_admin', 'admin', 'manager', 'agent'].includes(user?.role ?? '')
              ? `/grc/alertes/${alerte.id}`
              : `/mes-alertes/${alerte.id}`
          }
          className="inline-flex items-center gap-1 rounded-r-md border border-white/25 bg-white/15 px-2.5 py-1 text-xs font-medium backdrop-blur-glass transition-all hover:bg-white/25 focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-1 focus:ring-offset-transparent"
        >
          Détails <ChevronRight className="h-3 w-3" aria-hidden="true" />
        </Link>

        <button
          type="button"
          onClick={dismiss}
          aria-label="Masquer ce bandeau pour cette session"
          className="rounded-r-md p-1.5 hover:bg-white/20 focus:outline-none focus:ring-2 focus:ring-white/60 focus:ring-offset-1 focus:ring-offset-transparent transition-colors"
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
